import { lumi } from '@/lib/lumi'

export type ProjectPostKind = 'curve' | 'presale'

export type ProjectPost = {
  id: string
  projectKey: string
  kind: ProjectPostKind
  wallet: string
  content: string
  image?: string
  createdAt: string
}

const AUTH_PREFIX = 'Penguin Pad: authorize news feed post'
const ADMIN_AUTH_PREFIX = 'Penguin Pad: authorize admin delete'
const AUTH_TTL_MS = 30 * 60 * 1000

const POST_CREATED_EVENT = 'penguin-pad-project-post-created'
const POST_DELETED_EVENT = 'penguin-pad-project-post-deleted'

function buildAuthMessage(wallet: string, prefix = AUTH_PREFIX) {
  const issued = new Date().toISOString()
  const expires = new Date(Date.now() + AUTH_TTL_MS).toISOString()
  return `${prefix}\n\nWallet: ${wallet}\nIssued: ${issued}\nExpires: ${expires}`
}

function invokeErrorMessage(error: unknown, fallback: string) {
  const record = error as { data?: { error?: string }, message?: string }
  return (record?.data?.error || record?.message || fallback).slice(0, 180)
}

function parsePosts(value: unknown): ProjectPost[] {
  if (!Array.isArray(value))
    return []
  const rows: ProjectPost[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object')
      continue
    const row = item as Record<string, unknown>
    const id = String(row.id ?? row._id ?? '')
    const projectKey = typeof row.projectKey === 'string' ? row.projectKey : ''
    const kind = row.kind === 'presale' ? 'presale' : 'curve'
    const wallet = typeof row.wallet === 'string' ? row.wallet : ''
    const content = typeof row.content === 'string' ? row.content : ''
    const image = typeof row.image === 'string' && row.image.startsWith('https://') ? row.image : undefined
    const createdAt = typeof row.createdAt === 'string' ? row.createdAt : ''
    if (!id || !projectKey || !wallet || (!content && !image))
      continue
    rows.push({ id, projectKey, kind, wallet, content, image, createdAt })
  }
  return rows
}

export async function listProjectPosts(projectKey?: string, limit = 30): Promise<ProjectPost[]> {
  const key = projectKey?.trim().toLowerCase()
  if (!key)
    return []
  try {
    const result = await lumi.functions.invoke('projectPosts', {
      method: 'POST',
      body: { action: 'list', projectKey: key, limit },
    }) as { ok?: boolean, posts?: unknown } | undefined
    if (!result || result.ok === false)
      return []
    return parsePosts(result.posts)
  }
  catch (error) {
    console.error('List project posts failed:', error)
    return []
  }
}

export async function createProjectPost(input: {
  projectKey: string
  kind: ProjectPostKind
  tokenAddress: string
  content: string
  image?: string
  wallet: string
  signMessage: (message: string) => Promise<string>
}): Promise<ProjectPost> {
  const projectKey = input.projectKey.trim().toLowerCase()
  const content = input.content.trim()
  const image = input.image?.trim()
  if (!projectKey)
    throw new Error('Missing project reference')
  if (!content && !image)
    throw new Error('Write something or add a photo before posting')
  if (!input.wallet)
    throw new Error('Connect your wallet to post')
  if (!input.tokenAddress)
    throw new Error('This token is still indexing on-chain. Try again shortly.')

  const message = buildAuthMessage(input.wallet)
  const signature = (await input.signMessage(message)).trim()
  if (!signature)
    throw new Error('Wallet signature was rejected')

  try {
    const result = await lumi.functions.invoke('projectPosts', {
      method: 'POST',
      body: {
        action: 'create',
        wallet: input.wallet,
        message,
        signature,
        kind: input.kind,
        projectKey,
        tokenAddress: input.tokenAddress,
        content,
        image,
      },
    }) as { ok?: boolean, error?: string, post?: unknown } | undefined
    if (!result || result.ok === false || !result.post)
      throw new Error(result?.error || 'Could not publish this post')
    const [post] = parsePosts([result.post])
    if (!post)
      throw new Error('Could not publish this post')
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(POST_CREATED_EVENT, { detail: { projectKey, post } }))
    }
    return post
  }
  catch (error) {
    console.error('Create project post failed:', error)
    throw new Error(invokeErrorMessage(error, 'Could not publish this post'))
  }
}

export async function deleteProjectPost(input: {
  postId: string
  wallet: string
  signMessage: (message: string) => Promise<string>
}): Promise<void> {
  const wallet = input.wallet?.trim().toLowerCase() || ''
  const postId = input.postId.trim()
  if (!wallet)
    throw new Error('Connect the admin wallet to delete')
  if (!postId)
    throw new Error('Missing post reference')

  const message = buildAuthMessage(wallet, ADMIN_AUTH_PREFIX)
  const signature = (await input.signMessage(message)).trim()
  if (!signature)
    throw new Error('Wallet signature was rejected')

  try {
    const result = await lumi.functions.invoke('projectPosts', {
      method: 'POST',
      body: { action: 'delete', postId, wallet, message, signature },
    }) as { ok?: boolean, error?: string } | undefined
    if (!result || result.ok === false)
      throw new Error(result?.error || 'Could not delete this post')
    if (typeof window !== 'undefined')
      window.dispatchEvent(new CustomEvent(POST_DELETED_EVENT, { detail: { postId } }))
  }
  catch (error) {
    console.error('Delete project post failed:', error)
    throw new Error(invokeErrorMessage(error, 'Could not delete this post'))
  }
}

export function onProjectPostCreated(listener: (event: { projectKey: string, post: ProjectPost }) => void) {
  if (typeof window === 'undefined')
    return () => {}
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<{ projectKey?: string, post?: ProjectPost }>).detail
    if (!detail?.projectKey || !detail.post)
      return
    listener({ projectKey: detail.projectKey, post: detail.post })
  }
  window.addEventListener(POST_CREATED_EVENT, handler)
  return () => window.removeEventListener(POST_CREATED_EVENT, handler)
}

export function onProjectPostDeleted(listener: (postId: string) => void) {
  if (typeof window === 'undefined')
    return () => {}
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<{ postId?: string }>).detail
    if (!detail?.postId)
      return
    listener(detail.postId)
  }
  window.addEventListener(POST_DELETED_EVENT, handler)
  return () => window.removeEventListener(POST_DELETED_EVENT, handler)
}
